import React, { useEffect, useRef, useState } from 'react';
import { useCart, CartItem, ProductType } from '../../context/CartContext';
import { useAuth } from '../../auth/AuthContext';
import { calculateBoxWeight, calculateTotal } from '../../toolkit/shipping';
import { formatCustomizationForDisplay } from '../../utils/customizationUtils';
import './styles.css';

// Helper function to generate UUID without external dependencies
const generateUUID = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};


const Checkout: React.FC = () => {
  const { isAuthenticated, isLoading: isAuthLoading, profile } = useAuth();
  const { 
    cart, 
    clearCart, 
    customerInfo, 
    shippingAddress,
    setCustomerInfo, 
    setShippingAddress,
    setOrderInfo: setOrderInfo,
    orderInfo 
  } = useCart();
  
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [isPickup, setIsPickup] = useState(false);
  const [useDifferentShippingAddress, setUseDifferentShippingAddress] = useState(false);
  const [freeShipping, setFreeShipping] = useState(false);
  const [amount_left_for_free_shipping, setAmountLeftForFreeShipping] = useState(0);
  const hasEditedName = useRef(false);
  const hasEditedEmail = useRef(false);

  // Prefer the authenticated member profile, while preserving anything the
  // customer has already typed into this checkout session.
  useEffect(() => {
    if (!hasEditedName.current) {
      setName((isAuthenticated ? profile?.name : '') || customerInfo?.name || '');
    }
    if (!hasEditedEmail.current) {
      setEmail((isAuthenticated ? profile?.email : '') || customerInfo?.email || '');
    }
  }, [customerInfo, isAuthenticated, profile]);

  const profileShippingAddress = profile?.shippingAddress;
  const hasShippingAddressOnFile = Boolean(
    isAuthenticated
    && profileShippingAddress?.addressLine1
    && profileShippingAddress.city
    && profileShippingAddress.state
    && profileShippingAddress.zipCode
  );

  useEffect(() => {
    if (isAuthenticated && profileShippingAddress) {
      if (!useDifferentShippingAddress) {
        setAddressLine1(profileShippingAddress.addressLine1);
        setAddressLine2(profileShippingAddress.addressLine2);
        setCity(profileShippingAddress.city);
        setState(profileShippingAddress.state);
        setZipCode(profileShippingAddress.zipCode);
      }
      return;
    }

    if (!isAuthenticated && shippingAddress) {
      setAddressLine1(shippingAddress.addressLine1);
      setAddressLine2(shippingAddress.addressLine2);
      setCity(shippingAddress.city);
      setState(shippingAddress.state);
      setZipCode(shippingAddress.zipCode);
    }
  }, [
    isAuthenticated,
    profileShippingAddress,
    shippingAddress,
    useDifferentShippingAddress,
  ]);

  const handleDifferentShippingAddressChange = (checked: boolean) => {
    setUseDifferentShippingAddress(checked);
    if (checked) {
      setAddressLine1('');
      setAddressLine2('');
      setCity('');
      setState('');
      setZipCode('');
      return;
    }

    if (profileShippingAddress) {
      setAddressLine1(profileShippingAddress.addressLine1);
      setAddressLine2(profileShippingAddress.addressLine2);
      setCity(profileShippingAddress.city);
      setState(profileShippingAddress.state);
      setZipCode(profileShippingAddress.zipCode);
    }
  };

  // Keep the cart's persisted customer identity aligned with the authenticated
  // member record. Older localStorage entries remain valid because memberId is optional.
  useEffect(() => {
    if (isAuthenticated && profile?.memberId) {
      const nextCustomerInfo = {
        name: name || profile.name || '',
        email: email || profile.email || '',
        memberId: profile.memberId,
      };
      if (
        customerInfo?.name !== nextCustomerInfo.name
        || customerInfo?.email !== nextCustomerInfo.email
        || customerInfo?.memberId !== nextCustomerInfo.memberId
      ) {
        setCustomerInfo(nextCustomerInfo);
      }
      return;
    }

    if (!isAuthLoading && !isAuthenticated && customerInfo?.memberId) {
      setCustomerInfo({
        name: customerInfo.name,
        email: customerInfo.email,
      });
    }
  }, [
    customerInfo,
    email,
    isAuthLoading,
    isAuthenticated,
    name,
    profile,
    setCustomerInfo,
  ]);

  // Prevent caching of checkout page
  useEffect(() => {
    // Add cache-busting timestamp to prevent browser caching
    const timestamp = Date.now();
    const url = new URL(window.location.href);
    if (!url.searchParams.has('_t')) {
      url.searchParams.set('_t', timestamp.toString());
      window.history.replaceState({}, '', url.toString());
    }
    
    // Force browser to not use cached version
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => {
          caches.delete(name);
        });
      });
    }
  }, []);

  useEffect(() => {
    if (cart.length > 0 && cart.reduce((sum, item) => sum + item.price * item.quantity, 0) >= 100) {
      setFreeShipping(true);
      setAmountLeftForFreeShipping(100 - cart.reduce((sum, item) => sum + item.price * item.quantity, 0));
    } else {
      setFreeShipping(false);
    }
  }, [cart]);

  // BOGO feature expiration date: February 1, 2026
  //const BOGO_EXPIRATION_DATE = new Date('2026-02-01T00:00:00Z');
  const isBOGOEnabled = false; //new Date() < BOGO_EXPIRATION_DATE;

  // BOGO calculation: Buy One Get One Free - ONLY applies to snapcharm products
  // For every 2 snapcharm items purchased, get 1 free (maximum 3 free items total)
  // Feature disabled after February 1, 2026
  const calculateBOGO = () => {
    // If BOGO is disabled, return empty results
    if (!isBOGOEnabled) {
      return { freeQuantities: {}, totalFreeItems: 0 };
    }

    // Filter cart to only include snapcharm products for BOGO calculation
    const snapCharmItems = cart.filter(item => item.product_type === ProductType.SNAPCHARM);

    // Map to track items by price
    const itemsByPrice: { [price: number]: Array<{ item: CartItem; index: number; freeQuantity: number }> } = {};

    // Group snapcharm items by price
    snapCharmItems.forEach((item, index) => {
      if (!itemsByPrice[item.price]) {
        itemsByPrice[item.price] = [];
      }
      itemsByPrice[item.price].push({ item, index, freeQuantity: 0 });
    });

    // Calculate free quantities
    let totalFreeItems = 0;
    const MAX_FREE_ITEMS = 3;

    // For each price point, calculate free items
    Object.keys(itemsByPrice).forEach(priceStr => {
      const price = parseFloat(priceStr);
      const itemsAtPrice = itemsByPrice[price];

      // Calculate total units at this price
      const totalUnits = itemsAtPrice.reduce((sum, entry) => sum + entry.item.quantity, 0);

      // Traditional BOGO: for every 2 items purchased, get 1 free
      const potentialFreeItems = Math.floor(totalUnits / 2);
      const freeItemsRemaining = MAX_FREE_ITEMS - totalFreeItems;

      if (freeItemsRemaining > 0 && potentialFreeItems > 0) {
        // Calculate how many free items we can actually give at this price point
        const freeToAllocate = Math.min(potentialFreeItems, freeItemsRemaining);

        // Distribute free items across items at this price point
        // Prefer to make items free starting from the end (newer items)
        let remainingFree = freeToAllocate;

        // Allocate free items starting from the last item (prefer newer items to be free)
        for (let i = itemsAtPrice.length - 1; i >= 0 && remainingFree > 0; i--) {
          const entry = itemsAtPrice[i];
          // This item can contribute up to its quantity to free items
          const freeForThisItem = Math.min(entry.item.quantity, remainingFree);
          entry.freeQuantity = freeForThisItem;
          remainingFree -= freeForThisItem;
          totalFreeItems += freeForThisItem;
        }
      }
    });

    // Create a map of item IDs to free quantities
    const freeQuantities: { [itemId: string]: number } = {};
    Object.values(itemsByPrice).forEach(entries => {
      entries.forEach(entry => {
        if (entry.freeQuantity > 0) {
          freeQuantities[entry.item.id] = entry.freeQuantity;
        }
      });
    });

    return { freeQuantities, totalFreeItems };
  };

  const { freeQuantities, totalFreeItems } = calculateBOGO();

  // Calculate total excluding free items
  const total = cart.reduce((sum, item) => {
    const freeQty = freeQuantities[item.id] || 0;
    const paidQty = Math.max(0, item.quantity - freeQty);
    return sum + item.price * paidQty;
  }, 0);

  const content_weight_grams = cart.reduce((sum, item) => sum + item.weight_grams * item.quantity, 0);
  const total_items = cart.reduce((sum, item) => sum + item.quantity, 0);
  const { tax: total_tax, shipping: base_shipping, total: base_total } = calculateTotal(total, total_items, zipCode, state, isPickup, content_weight_grams);
  const shipping_cost = isPickup ? 0 : base_shipping;
  const grand_total = isPickup ? (total + total_tax) : base_total;

  const hogcloud_payment_handler_url = process.env.REACT_APP_HOGCLOUD_PAYMENT_HANDLER_URL || "";

  const box_weight_grams = calculateBoxWeight(total_items);

  const total_weight_grams = content_weight_grams + box_weight_grams;

  //round to 2 decimal places
  const total_weight_oz = Math.round(total_weight_grams / 28.3495 * 100) / 100;
  const handlePay = async () => {
    if (!hogcloud_payment_handler_url) {
      alert('Payment handler URL is not configured. Please contact support.');
      return;
    }
    
    if (!email || !name) {
      alert('Please enter your name and email.');
      return;
    }
    
    if (!isPickup && (!addressLine1 || !city || !state || !zipCode)) {
      alert('Please enter your complete shipping address.');
      return;
    }
    
    setLoading(true);
        
    try {
      // Generate order ID
      const memberId = isAuthenticated ? profile?.memberId : undefined;
      const customerData = { name, email, memberId };
      
      // Save to session
      setCustomerInfo(customerData);
      if (!isPickup) {
        const shippingData = {
          addressLine1,
          addressLine2,
          city,
          state,
          zipCode
        };
        setShippingAddress(shippingData);
      }
      setOrderInfo({
        isPickup: isPickup,
        checkout_request_id: generateUUID(),
        createdAt: new Date()
      });

      // Get API key from environment variable with fallback for development
      const PAYMENT_HANDLER_API_KEY = process.env.REACT_APP_PAYMENT_HANDLER_API_KEY || "";
      const x_api_key = btoa(PAYMENT_HANDLER_API_KEY);
      const hogcloud_payment_handler_url = process.env.REACT_APP_HOGCLOUD_PAYMENT_HANDLER_URL || "";
      
      // Get the checkout_request_id from the order info
      const checkout_request_id = orderInfo?.checkout_request_id || generateUUID();
      
      // Build shipping address object only for shipping orders with valid data
      let shippingAddressData = null;
      if (!isPickup && addressLine1 && city && state && zipCode) {
        shippingAddressData = {
          addressLine1,
          addressLine2,
          city,
          state,
          zipCode
        };
      }

      // Adjust cart items for BOGO - split items with free quantities into paid and free line items
      // Only apply BOGO if feature is enabled
      const adjustedCartItems: CartItem[] = isBOGOEnabled ? (() => {
        const items: CartItem[] = [];
        cart.forEach(item => {
          const freeQty = freeQuantities[item.id] || 0;
          const paidQty = item.quantity - freeQty;
          
          if (paidQty > 0) {
            // Add paid items with original price
            items.push({ ...item, quantity: paidQty });
          }
          
          if (freeQty > 0) {
            // Add free items with price 0 and note in name
            // Use unique ID by appending "-BOGO-FREE" to prevent database key conflicts
            items.push({
              ...item,
              id: `${item.id}-BOGO-FREE`,
              quantity: freeQty,
              price: 0,
              name: `${item.name} (BOGO - FREE)`
            });
          }
        });
        return items;
      })() : cart;
      
      const response = await fetch(hogcloud_payment_handler_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': x_api_key
        },
        body: JSON.stringify({
          items: adjustedCartItems,
          email: email,
          name: name,
          memberId: memberId,
          shippingAddress: shippingAddressData,
          taxAmount: total_tax,
          shippingAmount: shipping_cost,
          totalAmount: grand_total,
          totalWeight: total_weight_grams,
          isPickup: isPickup,
          checkout_request_id: checkout_request_id
        })
      });

      // Check if response is ok before trying to parse JSON
      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        
        try {
          // Try to get error details from response body
          const errorText = await response.text();
          if (errorText) {
            // If it's JSON, try to parse it
            try {
              const errorData = JSON.parse(errorText);
              errorMessage = errorData.message || errorData.error || errorMessage;
            } catch {
              // If not JSON, use the text as is (truncated if too long)
              errorMessage = errorText.length > 100 ? errorText.substring(0, 100) + '...' : errorText;
            }
          }
        } catch (textError) {
          console.warn('Could not read error response body:', textError);
        }
        
        throw new Error(`Payment handler error: ${errorMessage}`);
      }

      const data = await response.json();
      
      if (data.url) {
        // Redirect to Stripe Checkout
        window.location.href = data.url;
      } else {
        throw new Error(data.message || 'Payment initiation failed - no checkout URL received');
      }
    } catch (error: unknown) {
      console.error('Payment error:', error);
      
      // Show more helpful error message
      let errorMessage = 'Payment initiation failed. Please try again.';
      
      if (error instanceof Error && error.message) {
        if (error.message.includes('502')) {
          errorMessage = 'Payment service is temporarily unavailable. Please try again in a few minutes.';
        } else if (error.message.includes('Payment handler error:')) {
          errorMessage = error.message.replace('Payment handler error: ', '');
        } else if (error.message.includes('Failed to fetch')) {
          errorMessage = 'Unable to connect to payment service. Please check your internet connection.';
        }
      }
      
      alert(errorMessage);
    }
    setLoading(false);
  };

  return (
    <div className="invoice-container">

      {cart.length === 0 ? (
        <p>Your cart is empty.</p>
      ) : (
        <div className="invoice-content">
          <div className="invoice-details">
            <div className="customer-info">
              <h3>Bill To:</h3>
              <div className="form-group">
                <label>Name:</label>
                <input 
                  value={name} 
                  onChange={e => {
                    hasEditedName.current = true;
                    setName(e.target.value);
                  }}
                  placeholder="Enter your name"
                />
              </div>
              <div className="form-group">
                <label>Email:</label>
                <input 
                  type="email" 
                  value={email} 
                  onChange={e => {
                    hasEditedEmail.current = true;
                    setEmail(e.target.value);
                  }}
                  placeholder="Enter your email"
                />
                <br/>
                <b>Please ensure your email address is correct before submitting your order*</b>
              </div>

            </div>

            <div className="pickup-option">
              <div className="form-group">
                <label className="checkbox-label">
                  <input 
                    type="checkbox" 
                    checked={isPickup} 
                    onChange={e => setIsPickup(e.target.checked)}
                  />
                  Pickup in Fayetteville
                </label>
              </div>
            </div>

            {isPickup && (
              <div className="pickup-info">
                <h3>Pickup at Fayetteville Public Library</h3>
                <p><b>South Parking Lot</b><br/>Wednesday and Fridays<br/>12:00 PM - 1:00 PM</p>
              </div>
            )}

            {!isPickup && (<div className="shipping-info">
              {hasShippingAddressOnFile && (
                <div className="different-shipping-option">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={useDifferentShippingAddress}
                      onChange={e => handleDifferentShippingAddressChange(e.target.checked)}
                    />
                    Ship to a different address than my address on file
                  </label>
                </div>
              )}
              <div className="shipping-info-note">
                <p><strong>📦 Packaging:</strong> All items are carefully packaged in either bubble mailers or boxes. <a href="/shipping" className="shipping-link">More info</a></p>
                <div className="summary-row">
                  <span>Packaging Weight: {total_weight_oz}oz [ Contents: {content_weight_grams}g, Packaging: {box_weight_grams}g]</span>
                </div>
              </div>
              <h3>Shipping Address:</h3>

              <div className="form-group">
                <label>Address Line 1:</label>
                <input 
                  value={addressLine1} 
                  onChange={e => setAddressLine1(e.target.value)}
                  placeholder="Street address"
                  disabled={isPickup}
                />
              </div>
              <div className="form-group">
                <label>Address Line 2:</label>
                <input 
                  value={addressLine2} 
                  onChange={e => setAddressLine2(e.target.value)}
                  placeholder="Apt, suite, etc. (optional)"
                  disabled={isPickup}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>City:</label>
                  <input 
                    value={city} 
                    onChange={e => setCity(e.target.value)}
                    placeholder="City"
                    disabled={isPickup}
                  />
                </div>
                <div className="form-group">
                  <label>State:</label>
                  <input 
                    value={state} 
                    onChange={e => setState(e.target.value)}
                    placeholder="State"
                    disabled={isPickup}
                  />
                </div>
                <div className="form-group">
                  <label>Zip Code:</label>
                  <input 
                    value={zipCode} 
                    onChange={e => setZipCode(e.target.value)}
                    placeholder="Zip code"
                    disabled={isPickup}
                  />
                </div>
              </div>
            </div>
            )}
          </div>

          <div className="invoice-items">
            <table>
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Quantity</th>
                  <th>Price</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {cart.map(item => {
                  const freeQty = freeQuantities[item.id] || 0;
                  const paidQty = item.quantity - freeQty;
                  const itemTotal = item.price * paidQty;
                  return (
                    <tr key={item.id}>
                      <td>
                        {item.name}
                        {freeQty > 0 && (
                          <span style={{ color: '#28a745', fontWeight: 'bold', marginLeft: '8px' }}>
                            (BOGO - {freeQty} free)
                          </span>
                        )}
                        {item.is_customizeable && item.customization_items && (
                          <div className="customization-details">
                            ({formatCustomizationForDisplay(item.customization_items)})
                          </div>
                        )}
                      </td>
                      <td>
                        {item.quantity}
                        {freeQty > 0 && (
                          <span style={{ display: 'block', fontSize: '0.85em', color: '#28a745' }}>
                            ({freeQty} free)
                          </span>
                        )}
                      </td>
                      <td>
                        {freeQty > 0 && paidQty < item.quantity ? (
                          <>
                            <span style={{ textDecoration: 'line-through', color: '#999' }}>
                              ${item.price.toFixed(2)}
                            </span>
                            <span style={{ marginLeft: '4px', color: '#28a745' }}>
                              ${freeQty === item.quantity ? '0.00' : item.price.toFixed(2)}
                            </span>
                          </>
                        ) : (
                          `$${item.price.toFixed(2)}`
                        )}
                      </td>
                      <td>
                        {freeQty > 0 ? (
                          <>
                            <span style={{ textDecoration: 'line-through', color: '#999' }}>
                              ${(item.price * item.quantity).toFixed(2)}
                            </span>
                            <span style={{ marginLeft: '4px', color: '#28a745', fontWeight: 'bold' }}>
                              ${itemTotal.toFixed(2)}
                            </span>
                          </>
                        ) : (
                          `$${(item.price * item.quantity).toFixed(2)}`
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {isBOGOEnabled && totalFreeItems === 0 && cart.length > 0 && (
            <div className="invoice-summary" style={{ backgroundColor: '#e7f3ff', padding: '12px', borderRadius: '4px', marginBottom: '16px' }}>
              <p style={{ margin: 0, color: '#0066cc', fontWeight: '500' }}>
                You are eligible for up to 3 free BOGO items !
              </p>
            </div>
          )}
          <div className="invoice-summary">
            <h3 className="discounts-notice">ALL DISCOUNTS APPLIED AT CHECKOUT</h3>
          </div>
          {!freeShipping && (
            <div className="invoice-summary">
              <h3>Free shipping on purchases over $100!</h3>
            </div>
          )}
          <div className="invoice-summary">
            <div className="summary-row">
              <span>Subtotal:</span>
              <span>${total.toFixed(2)}</span>
            </div>
            <div className="summary-row">
              <span>Tax:</span>
              <span>${total_tax.toFixed(2)}</span>
            </div>
            {freeShipping ? (
              <div className="summary-row">
                <span>Shipping:</span>
                <span>Free! You've earned it.</span>
              </div>
            ) : (
              <div className="summary-row">
                <span>Shipping:</span>
                <span>${shipping_cost.toFixed(2)}</span>
              </div>
            )}
            <div className="summary-row total">
              <span>Total:</span>
              <span>${grand_total.toFixed(2)}</span>
            </div>
          </div>

          <div className="invoice-actions">
            <button 
              className="continue-shopping-button" 
              onClick={() => window.history.back()}
            >
              Shop
            </button>
            <button 
              className="pay-button" 
              onClick={handlePay} 
              disabled={loading}
            >
              {loading ? 'Processing...' : 'Pay Now'}
            </button>
          </div>
          <div className="invoice-footer">
            <p><i>You will be added to our mailing list. You can unsubscribe at any time</i></p>
          </div>
        </div>

      )}
    </div>
  );
};

export default Checkout;
